import {isValidHttpUrl} from '../../../utils/is-valid-http-url'

/**
 * A single web-search reference extracted from a `web_search` tool result.
 */
export type WebSearchReferenceItem = {
  title: string
  url: string
  excerpt?: string
}

const MAX_REFERENCES = 20

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/**
 * Parses the `web_search` tool result into clickable reference items.
 *
 * The built-in `github-mcp-server` web search returns an OpenAI-Responses
 * `output_text`: `{ (text.)value, annotations: [{ url_citation: { title, url } }] }`.
 * A simpler `{ results: [{ title, url, excerpt | snippet }] }` shape is also
 * accepted. References are de-duplicated by URL and capped. Parsing is
 * fail-closed: any structural mismatch yields an empty list, so callers fall
 * back to rendering the raw output.
 */
export function parseWebSearchReferences(output: string | undefined): WebSearchReferenceItem[] {
  if (!output) return []

  let parsed: unknown
  try {
    parsed = JSON.parse(output)
  } catch {
    return []
  }

  const items: WebSearchReferenceItem[] = []
  const seen = new Set<string>()

  const add = (url: unknown, title: unknown, excerpt?: unknown) => {
    // Tool output is untrusted; only accept absolute http(s) URLs so unsafe
    // schemes (e.g. `javascript:`, `data:`) never reach an `href`.
    if (typeof url !== 'string' || !isValidHttpUrl(url) || seen.has(url)) return
    seen.add(url)
    items.push({
      url,
      title: typeof title === 'string' && title.length > 0 ? title : url,
      ...(typeof excerpt === 'string' && excerpt.length > 0 ? {excerpt} : {}),
    })
  }

  const root = asRecord(parsed)

  // Shape A: OpenAI-Responses output_text with url_citation annotations.
  const annotationContainer = asRecord(root?.text) ?? root
  for (const annotation of asArray(annotationContainer?.annotations)) {
    const record = asRecord(annotation)
    const citation = asRecord(record?.url_citation) ?? record
    add(citation?.url, citation?.title)
  }

  // Shape B: { results: [{ title, url, excerpt | snippet }] }.
  for (const result of asArray(root?.results)) {
    const record = asRecord(result)
    add(record?.url, record?.title, record?.excerpt ?? record?.snippet)
  }

  return items.slice(0, MAX_REFERENCES)
}
